/**
 * Kazakhstan BIN/IIN control-digit validation.
 *
 * A BIN is exactly 12 digits. The 12th digit is a control digit computed from
 * the first 11 digits:
 *   1. control = Σ(dᵢ · w1ᵢ) mod 11, with the first weight set.
 *   2. If control === 10, recompute with the second weight set:
 *      control = Σ(dᵢ · w2ᵢ) mod 11.
 *   3. If control is still 10, the BIN is invalid.
 *   4. Otherwise the BIN is valid iff control === d₁₂ (the 12th digit).
 *
 * The value is always handled as a string to preserve leading zeroes; numeric
 * conversion is never used. Server-side validation remains authoritative — this
 * mirror only improves UX.
 *
 * Sources (via decision "Поля короткой заявки бизнеса на PromoLanding"):
 * adilet.zan.kz V2300032918 (BIN formation), V14E0010153 (12/12 control).
 */

const WEIGHTS_1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;
const WEIGHTS_2 = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2] as const;

const BIN_PATTERN = /^[0-9]{12}$/;

function controlDigit(digits: Array<number>, weights: ReadonlyArray<number>): number {
  let sum = 0;
  for (let i = 0; i < 11; i += 1) {
    sum += (digits[i] ?? 0) * (weights[i] ?? 0);
  }
  return sum % 11;
}

/** True when `value` is exactly 12 digits and passes the KZ control digit. */
export function isValidBin(value: string): boolean {
  if (!BIN_PATTERN.test(value)) return false;

  const digits = Array.from(value, (ch) => ch.charCodeAt(0) - 48);

  let control = controlDigit(digits, WEIGHTS_1);
  if (control === 10) {
    control = controlDigit(digits, WEIGHTS_2);
  }
  if (control === 10) return false;

  return control === digits[11];
}
